import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import LightBoxComponent from '../../../../components/common/LightBoxComponent';
import 'react-image-lightbox/style.css';

import { API_URL } from '../../../../utils/constants';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import GenericProductsApi from '../../../../services/api/GenericProductsApi';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import FileInputComponent from '../../../../components/common/forms/FileInput/FileInputComponent';
import validationSchema from '../validationSchema';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import ChoosingManufacturer from '../components/ChoosingManufacturer';
import PhotosApi from '../../../../services/api/PhotosApi';
import ButtonComponent from '../../../../components/common/forms/Button/ButtonComponent';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import TextAreaComponent from '~/src/components/common/forms/TextInput/TextAreaComponent';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import styled from 'styled-components';
import PrimaryButton from '~/src/components/common/forms/Button/PrimaryButton';

export default function GenericProductEdit(): JSX.Element {
  const { id } = useParams();
  const [itemData, setItemData] = useState({
    dataArrived: false,
    name: '',
    description: '',
    internalNotes: '',
    reserveMinimum: '',
    price: '',
    vat: '',
    photoFront: [],
    photoBack: [],
    otherPhotos: [],
    brandId: '',
    categoryId: '',
    hsCodeId: '',
    products: [],
  });
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    if (id) {
      const res = await GenericProductsApi.update(id, values);
      if (res.status === StatusCodes.OK) {
        history.push(`/products`);
      }
    } else {
      const res = await GenericProductsApi.create(values);
      if (res.status === StatusCodes.OK) {
        history.push(`/products/edit/${res.data.id}`);
      }
    }
    setSubmitting(false);
  };

  const onHandleChange = (event, setFieldValue) => {
    const { files } = event.target;
    setFieldValue('logo', files);
  };

  const getPhotos = async () => {
    const res = await PhotosApi.getByGeneric(id);
    if (res.status === StatusCodes.OK) {
      const photos = [];
      res.data.forEach((photo) => {
        photos.push(`data:image/jpeg;base64, ${photo.image600.base64String}`);
      });
      setPhotoUrls(photos);
      setPhotos(res.data);
    }
  };

  const showPhotos = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const getItem = async () => {
    const res = await GenericProductsApi.get(id);
    const newItemData = { ...res.data, dataArrived: true };
    setItemData(newItemData);
    getPhotos();
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, [id]);

  return (
    <>
      <WhitePad isFluid>
        <TitleComponent
          title={`${id ? 'Edit ' : 'Create '} Generic Product`}
          titleLeft="Back"
          onLeftHandler={() => {
            history.push(`/products`);
          }}
        />
        {id && itemData.dataArrived === false ? null : (
          <Formik
            initialValues={{
              name: itemData.name,
              description: itemData.description || '',
              internalNotes: itemData.internalNotes || '',
              reserveMinimum: itemData.reserveMinimum || '',
              price: itemData.price || '',
              vat: itemData.vat || '',
              costToWarehouse: itemData.costToWarehouse || '',
              expirationDays: itemData.expirationDays || '',
              leadingTime: itemData.leadingTime || '',
              photoFront: itemData.photoFront,
              photoBack: itemData.photoBack,
              otherPhotos: itemData.otherPhotos,
              brandId: itemData.brandId,
              categoryId: itemData.categoryId,
              hsCodeId: itemData.hsCodeId,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              saveItem(values, setSubmitting);
            }}
          >
            {({ values, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, errors }) => (
              <FormWrapper width="100%">
                <form onSubmit={handleSubmit} autoComplete="off">
                  <FormRow title="General Information" />
                  <FormRow>
                    <TextInputComponent
                      name="name"
                      label="Name"
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={12}
                    />
                  </FormRow>
                  <FormRow>
                    <TextAreaComponent
                      name="description"
                      value={values.description}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      rows={4}
                    />
                    <TextAreaComponent
                      name="internalNotes"
                      value={values.internalNotes}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      rows={4}
                    />
                  </FormRow>
                  <FormRow>
                    <AutocompleteComponent
                      url={`${API_URL}/products/brands/get`}
                      name="brandId"
                      label="brandId"
                      initialValue={values.brandId}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.name}`}
                      queryFields={['name']}
                      errorMessage={errors.brandId}
                    />
                    <AutocompleteComponent
                      url={`${API_URL}/products/categories/get`}
                      name="categoryId"
                      label="categoryId"
                      initialValue={values.categoryId}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.name}`}
                      errorMessage={errors.categoryId}
                    />
                  </FormRow>
                  <FormRow>
                    <AutocompleteComponent
                      url={`${API_URL}/products/hscodes/get`}
                      name="hsCodeId"
                      label="hsCodeId"
                      initialValue={values.hsCodeId}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.code} - ${option.nameEn}`}
                      queryFields={['code', 'nameEn']}
                      errorMessage={errors.hsCodeId}
                    />
                    <TextInputComponent
                      name="price"
                      label="price"
                      value={values.price}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={2}
                      type="number"
                    />
                    <TextInputComponent
                      name="vat"
                      label="vat"
                      value={values.vat}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={2}
                      type="number"
                    />
                    <TextInputComponent
                      name="reserveMinimum"
                      label="reserveMinimum"
                      value={values.reserveMinimum}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      cols={2}
                      type="number"
                    />
                  </FormRow>
                  <FormRow title="Images">
                    <FileInputComponent
                      name="photoFront"
                      value={values.photoFront}
                      onBlur={handleBlur}
                      onChange={onHandleChange}
                      setFieldValue={setFieldValue}
                      cols={4}
                    />
                    <FileInputComponent
                      name="photoBack"
                      value={values.photoBack}
                      onBlur={handleBlur}
                      onChange={onHandleChange}
                      setFieldValue={setFieldValue}
                      cols={4}
                    />
                    <FileInputComponent
                      name="otherPhotos"
                      value={values.otherPhotos}
                      onBlur={handleBlur}
                      onChange={onHandleChange}
                      setFieldValue={setFieldValue}
                      cols={4}
                    />
                  </FormRow>
                  <FormRow title="Images From App">
                    {photos.map((photo, index) => {
                      return (
                        <button onClick={() => showPhotos(index)} type="button" style={{ cursor: 'pointer' }}>
                          <img
                            src={`data:image/jpeg;base64, ${photo.image600.base64String}`}
                            width="200"
                            height="200"
                            alt={photo.id}
                          />
                        </button>
                      );
                    })}
                  </FormRow>
                  <PrimaryButton isSubmitting={isSubmitting} type="submit" title="Save" />
                </form>
              </FormWrapper>
            )}
          </Formik>
        )}
      </WhitePad>
      <Wrapper>
        <WhitePad isFluid>
          <div style={{ padding: '25px' }}>
            {itemData.dataArrived && (
              <ChoosingManufacturer genericProductId={id} initialProducts={itemData.products} />
            )}
          </div>
        </WhitePad>
      </Wrapper>
      {photos && (
        <LightBoxComponent
          photos={photoUrls}
          photoIndex={photoIndex}
          setPhotoIndex={setPhotoIndex}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  );
}

const Wrapper = styled.div`
  position: relative;
  z-index: 9999;
`;
